import React, { useState, useEffect } from "react";
import { useBusinessPlan } from "../App";
import { useCurrency } from "../contexts/CurrencyContext";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import ExportButton from "./ui/ExportButton";
import { toast } from "sonner";
import StrategicFrameworkLinker from "./cost/StrategicFrameworkLinker";
import { TooltipLabel } from "./ui/TooltipLabel";
import { costCenterTooltips, helpText } from "../utils/costCenterTooltips";
import { 
  Building2, 
  Users, 
  Plus, 
  Edit, 
  Trash2, 
  DollarSign,
  Target,
  Activity,
  BarChart3,
  PieChart,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Eye,
  RefreshCw,
  Settings,
  ArrowRight,
  ChevronRight
} from "lucide-react";
import { 
  TreeDataStructure,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CostCenterManagement = () => {
  const { currentPlan } = useBusinessPlan();
  const { formatCurrency: formatCurrencyContext } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [costCenters, setCostCenters] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [sfHierarchy, setSfHierarchy] = useState([]);
  const [selectedCostCenter, setSelectedCostCenter] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingCenter, setEditingCenter] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    type: 'department',
    parent_id: 'none',
    budget_annual: 0,
    manager: '',
    custom_attributes: {}
  });

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316'];
  
  const COST_CENTER_TYPES = [
    { value: 'department', label: 'Department' },
    { value: 'division', label: 'Division' },
    { value: 'team', label: 'Team' },
    { value: 'custom', label: 'Custom' }
  ];

  // Format currency
  const formatCurrency = (amount, compact = false) => {
    if (!amount) return '$0';
    
    if (compact && Math.abs(amount) >= 1000000) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        notation: 'compact',
        compactDisplay: 'short'
      }).format(amount);
    }
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Load cost centers
  const loadCostCenters = async () => {
    if (!currentPlan?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('Loading cost centers...');

      const [centersRes, allocationsRes, sfRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/cost-centers/${currentPlan.id}`),
        axios.get(`${BACKEND_URL}/api/cost-allocations/${currentPlan.id}`),
        axios.get(`${BACKEND_URL}/api/v3/cost-management/sf-hierarchy/${currentPlan.id}`)
      ]);

      setCostCenters(centersRes.data || []);
      setAllocations(allocationsRes.data || []);
      setSfHierarchy(sfRes.data?.hierarchy || []);
      
      console.log('✅ Cost centers and SF hierarchy loaded successfully');
      
    } catch (err) {
      console.error('Error loading cost centers:', err);
      setCostCenters([]);
      setAllocations([]);
      setSfHierarchy([]);
    } finally {
      setLoading(false);
    }
  };

  // Create cost center
  const createCostCenter = async () => {
    if (!currentPlan?.id || !formData.name || !formData.code) {
      alert('Please fill in required fields');
      return;
    }

    try {
      // Handle "none" parent selection
      const submitData = {
        ...formData,
        parent_id: formData.parent_id === "none" ? null : formData.parent_id
      };
      
      await axios.post(`${BACKEND_URL}/api/cost-centers?plan_id=${currentPlan.id}`, submitData);
      
      // Reset form and close dialog
      setFormData({
        name: '',
        code: '',
        description: '',
        type: 'department',
        parent_id: 'none',
        budget_annual: 0,
        manager: '',
        custom_attributes: {}
      });
      setShowCreateDialog(false);
      
      // Reload data
      await loadCostCenters();
      
    } catch (err) {
      console.error('Error creating cost center:', err);
      alert(err.response?.data?.detail || 'Error creating cost center');
    }
  };

  // Update cost center
  const updateCostCenter = async () => {
    if (!editingCenter || !formData.name || !formData.code) {
      alert('Please fill in required fields');
      return;
    }

    try {
      // Handle "none" parent selection
      const submitData = {
        ...formData,
        parent_id: formData.parent_id === "none" ? null : formData.parent_id
      };
      
      await axios.put(`${BACKEND_URL}/api/cost-centers/${editingCenter.id}`, submitData);
      
      setShowEditDialog(false);
      setEditingCenter(null);
      await loadCostCenters();
      
    } catch (err) {
      console.error('Error updating cost center:', err);
      alert(err.response?.data?.detail || 'Error updating cost center');
    }
  };

  // Delete cost center
  const deleteCostCenter = async (centerId) => {
    if (!confirm('Are you sure you want to delete this cost center?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/cost-centers/${centerId}`);
      await loadCostCenters();
    } catch (err) {
      console.error('Error deleting cost center:', err);
      alert(err.response?.data?.detail || 'Error deleting cost center');
    }
  };

  // Build hierarchical tree structure
  const buildHierarchy = (centers, parentId = null) => {
    return centers
      .filter(center => center.parent_id === parentId)
      .map(center => ({
        ...center,
        children: buildHierarchy(centers, center.id)
      }));
  };

  // Calculate metrics
  const calculateCenterMetrics = (centerId) => {
    const centerAllocations = allocations.filter(a => a.cost_center_id === centerId);
    const totalAllocated = centerAllocations.reduce((sum, a) => sum + (a.allocated_amount || 0), 0);
    
    return {
      allocatedAmount: totalAllocated,
      allocationCount: centerAllocations.length
    };
  };

  // Handle form input changes
  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Open edit dialog
  const openEditDialog = (center) => {
    setEditingCenter(center);
    setFormData({
      name: center.name,
      code: center.code,
      description: center.description || '',
      type: center.type,
      parent_id: center.parent_id || 'none',
      budget_annual: center.budget_annual || 0,
      manager: center.manager || '',
      custom_attributes: center.custom_attributes || {}
    });
    setShowEditDialog(true);
  };

  useEffect(() => {
    loadCostCenters();
  }, [currentPlan?.id]);

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-slate-600">Loading cost centers...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!currentPlan) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-16">
          <div className="text-center max-w-md">
            <Building2 className="h-16 w-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No Business Plan Selected</h3>
            <p className="text-slate-600 mb-6">
              Please select or create a business plan to access cost center management.
            </p>
            <Button onClick={() => window.location.href = '/dashboard'} className="bg-blue-600 hover:bg-blue-700">
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const hierarchicalCenters = buildHierarchy(costCenters);
  
  // Analytics data
  const centersByType = costCenters.reduce((acc, center) => {
    acc[center.type] = (acc[center.type] || 0) + 1;
    return acc;
  }, {});

  const typeChartData = Object.entries(centersByType).map(([type, count]) => ({
    type: type.charAt(0).toUpperCase() + type.slice(1),
    count
  }));

  const budgetData = costCenters.map(center => {
    const metrics = calculateCenterMetrics(center.id);
    return {
      name: center.name.length > 15 ? center.name.substring(0, 15) + '...' : center.name,
      budget: center.budget_annual || 0,
      allocated: metrics.allocatedAmount,
      variance: (center.budget_annual || 0) - metrics.allocatedAmount
    };
  }).slice(0, 8); // Top 8 for visibility

  // Loading state with skeleton
  if (loading) {
    return (
      <div className="p-8 space-y-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="h-8 w-64 bg-slate-200 rounded animate-pulse mb-2" />
            <div className="h-4 w-96 bg-slate-200 rounded animate-pulse" />
          </div>
          <div className="h-10 w-32 bg-slate-200 rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="h-16 bg-slate-200 rounded animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-24 bg-slate-200 rounded animate-pulse" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Cost Center Management</h1>
          <p className="text-slate-600 mt-1">
            Hierarchical cost center structure and allocation management for {currentPlan.company_name || currentPlan.name}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <ExportButton
            targetId="cost-center-management-content"
            componentName="Cost_Center_Management"
            formats={['png']}
            size="sm"
            variant="outline"
            showIcon={true}
            showText={true}
          />
          <Button onClick={loadCostCenters} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus className="h-4 w-4 mr-2" />
                Add Cost Center
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create New Cost Center</DialogTitle>
                <DialogDescription>
                  Add a new cost center to your organizational structure
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <TooltipLabel htmlFor="center-name" tooltip={costCenterTooltips.centerName}>
                      Name *
                    </TooltipLabel>
                    <Input
                      id="center-name"
                      value={formData.name}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      placeholder="e.g., Marketing Department"
                    />
                  </div>
                  <div className="space-y-2">
                    <TooltipLabel htmlFor="center-code" tooltip={costCenterTooltips.centerCode}>
                      Code *
                    </TooltipLabel>
                    <Input
                      id="center-code"
                      value={formData.code}
                      onChange={(e) => handleFormChange('code', e.target.value.toUpperCase())}
                      placeholder="e.g., MKTG-001"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <TooltipLabel htmlFor="center-description" tooltip={costCenterTooltips.centerDescription}>
                    Description
                  </TooltipLabel>
                  <Textarea
                    id="center-description"
                    value={formData.description}
                    onChange={(e) => handleFormChange('description', e.target.value)}
                    placeholder="Brief description of cost center purpose and responsibilities"
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <TooltipLabel htmlFor="center-type" tooltip={costCenterTooltips.centerType}>
                      Type
                    </TooltipLabel>
                    <Select value={formData.type} onValueChange={(value) => handleFormChange('type', value)}>
                      <SelectTrigger id="center-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COST_CENTER_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <TooltipLabel htmlFor="parent-center" tooltip={costCenterTooltips.parentCenter}>
                      Parent Cost Center
                    </TooltipLabel>
                    <Select value={formData.parent_id} onValueChange={(value) => handleFormChange('parent_id', value)}>
                      <SelectTrigger id="parent-center">
                        <SelectValue placeholder="Select parent (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None (Top Level)</SelectItem>
                        {costCenters.map(center => (
                          <SelectItem key={center.id} value={center.id}>{center.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <TooltipLabel htmlFor="annual-budget" tooltip={costCenterTooltips.annualBudget}>
                      Annual Budget (USD)
                    </TooltipLabel>
                    <Input
                      id="annual-budget"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.budget_annual}
                      onChange={(e) => handleFormChange('budget_annual', parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <TooltipLabel htmlFor="center-manager" tooltip={costCenterTooltips.centerManager}>
                      Manager
                    </TooltipLabel>
                    <Input
                      id="center-manager"
                      value={formData.manager}
                      onChange={(e) => handleFormChange('manager', e.target.value)}
                      placeholder="e.g., John Smith"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={createCostCenter} className="bg-blue-600 hover:bg-blue-700">
                  Create Cost Center
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Help/Info Card with Key Tips */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="p-6">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Settings className="h-5 w-5 text-blue-600" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Cost Center Management Tips</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
                <div className="space-y-2">
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Hierarchy Setup:</strong> {helpText.hierarchySetup}</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Allocation Strategy:</strong> {helpText.allocationStrategy}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Strategic Alignment:</strong> {helpText.strategicAlignment}</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Budget Tracking:</strong> {helpText.budgetTracking}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700 font-medium">Total Cost Centers</p>
                <p className="text-2xl font-bold text-blue-900">{costCenters.length}</p>
              </div>
              <Building2 className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700 font-medium">Total Budget</p>
                <p className="text-2xl font-bold text-green-900">
                  {formatCurrency(costCenters.reduce((sum, c) => sum + (c.budget_annual || 0), 0), true)}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-700 font-medium">Active Allocations</p>
                <p className="text-2xl font-bold text-purple-900">{allocations.length}</p>
              </div>
              <Target className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-700 font-medium">Allocated Amount</p>
                <p className="text-2xl font-bold text-orange-900">
                  {formatCurrency(allocations.reduce((sum, a) => sum + (a.allocated_amount || 0), 0), true)}
                </p>
              </div>
              <Activity className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div id="cost-center-management-content">
      {/* Main Content Tabs */}
      <Tabs defaultValue="hierarchy" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="hierarchy">Cost Center Hierarchy</TabsTrigger>
          <TabsTrigger value="analytics">Analytics & Reports</TabsTrigger>
          <TabsTrigger value="allocations">Cost Allocations</TabsTrigger>
          <TabsTrigger value="budgeting">Budget Management</TabsTrigger>
        </TabsList>

        {/* Hierarchy Tab */}
        <TabsContent value="hierarchy" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Organizational Structure</CardTitle>
              <CardDescription>Hierarchical view of your cost centers</CardDescription>
            </CardHeader>
            <CardContent>
              {costCenters.length === 0 ? (
                <div className="text-center py-8">
                  <Building2 className="h-16 w-16 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No Cost Centers Yet</h3>
                  <p className="text-slate-600 mb-4">
                    Create your first cost center to start organizing your expenses.
                  </p>
                  <Button onClick={() => setShowCreateDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Create First Cost Center
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {hierarchicalCenters.map((center, index) => (
                    <CostCenterNode 
                      key={center.id} 
                      center={center} 
                      level={0}
                      allocations={allocations}
                      onEdit={openEditDialog}
                      onDelete={deleteCostCenter}
                      formatCurrency={formatCurrency}
                      calculateCenterMetrics={calculateCenterMetrics}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Cost Center Types</CardTitle>
                <CardDescription>Distribution by type</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={typeChartData}
                        dataKey="count"
                        nameKey="type"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({type, count}) => `${type}: ${count}`}
                      >
                        {typeChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Budget vs Allocated</CardTitle>
                <CardDescription>Financial performance by cost center</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={budgetData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="name" stroke="#64748b" fontSize={11} />
                      <YAxis stroke="#64748b" />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Bar dataKey="budget" fill="#3b82f6" name="Budget" />
                      <Bar dataKey="allocated" fill="#10b981" name="Allocated" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Allocations Tab */}
        <TabsContent value="allocations" className="space-y-6">
          <CostAllocationManagement 
            costCenters={costCenters}
            allocations={allocations}
            currentPlan={currentPlan}
            formatCurrency={formatCurrency}
            onRefresh={loadCostCenters}
            sfHierarchy={sfHierarchy}
          />
        </TabsContent>

        {/* Budget Management Tab */}
        <TabsContent value="budgeting" className="space-y-6">
          <BudgetManagementView 
            costCenters={costCenters}
            allocations={allocations}
            currentPlan={currentPlan}
            formatCurrency={formatCurrency}
            onRefresh={loadCostCenters}
          />
        </TabsContent>
      </Tabs>
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Cost Center</DialogTitle>
            <DialogDescription>
              Update cost center information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Code *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => handleFormChange('code', e.target.value)}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={formData.type} onValueChange={(value) => handleFormChange('type', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COST_CENTER_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Parent Cost Center</Label>
                <Select value={formData.parent_id} onValueChange={(value) => handleFormChange('parent_id', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select parent (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (Top Level)</SelectItem>
                    {costCenters.filter(c => c.id !== editingCenter?.id).map(center => (
                      <SelectItem key={center.id} value={center.id}>{center.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Annual Budget</Label>
                <Input
                  type="number"
                  value={formData.budget_annual}
                  onChange={(e) => handleFormChange('budget_annual', parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label>Manager</Label>
                <Input
                  value={formData.manager}
                  onChange={(e) => handleFormChange('manager', e.target.value)}
                />
              </div>
            </div>
          </div>
          
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={updateCostCenter} className="bg-blue-600 hover:bg-blue-700">
              Update Cost Center
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Simplified components for compilation - full functionality can be restored later

// Placeholder components to prevent compilation errors
const CostAllocationManagement = ({ costCenters, allocations, currentPlan, formatCurrency, onRefresh, sfHierarchy }) => {
  const [newAllocation, setNewAllocation] = useState({
    cost_center_id: '',
    amount: 0,
    percentage: 0,
    allocation_type: 'fixed',
    description: '',
    linked_objective_id: '',
    linked_initiative_id: '',
    linked_revenue_stream_id: ''
  });
  const [editingAllocation, setEditingAllocation] = useState(null);
  const [showEditAllocation, setShowEditAllocation] = useState(false);

  const handleCreateAllocation = async () => {
    if (!newAllocation.cost_center_id) {
      toast.error('Please select a cost center');
      return;
    }
    
    try {
      // Pass plan_id as query parameter, not in body
      await axios.post(`${BACKEND_URL}/api/cost-allocations?plan_id=${currentPlan.id}`, {
        cost_item_id: 'general', // Use a placeholder since we're allocating to center directly
        cost_center_id: newAllocation.cost_center_id,
        allocation_method: newAllocation.allocation_type === 'fixed' ? 'direct' : 'percentage',
        allocated_amount: newAllocation.allocation_type === 'fixed' ? parseFloat(newAllocation.amount) : 0,
        allocation_percentage: newAllocation.allocation_type === 'percentage' ? parseFloat(newAllocation.percentage) : null,
        notes: newAllocation.description || null,
        linked_objective_id: newAllocation.linked_objective_id || null,
        linked_initiative_id: newAllocation.linked_initiative_id || null,
        linked_revenue_stream_id: newAllocation.linked_revenue_stream_id || null
      });
      setNewAllocation({
        cost_center_id: '',
        amount: 0,
        percentage: 0,
        allocation_type: 'fixed',
        description: '',
        linked_objective_id: '',
        linked_initiative_id: '',
        linked_revenue_stream_id: ''
      });
      toast.success('Cost allocation created successfully');
      onRefresh();
    } catch (error) {
      console.error('Error creating allocation:', error);
      toast.error(error.response?.data?.detail || 'Failed to create cost allocation');
    }
  };

  const handleEditAllocation = (allocation) => {
    setEditingAllocation(allocation);
    // Convert backend format to form format
    setNewAllocation({
      cost_center_id: allocation.cost_center_id,
      amount: allocation.allocated_amount || 0,
      percentage: allocation.allocation_percentage || 0,
      allocation_type: allocation.allocation_method === 'percentage' ? 'percentage' : 'fixed',
      description: allocation.notes || '',
      linked_objective_id: allocation.linked_objective_id || '',
      linked_initiative_id: allocation.linked_initiative_id || '',
      linked_revenue_stream_id: allocation.linked_revenue_stream_id || ''
    });
    setShowEditAllocation(true);
  };

  const handleUpdateAllocation = async () => {
    if (!newAllocation.cost_center_id) {
      toast.error('Please select a cost center');
      return;
    }

    try {
      await axios.put(`${BACKEND_URL}/api/cost-allocations/${editingAllocation.id}`, {
        cost_item_id: editingAllocation.cost_item_id || 'general',
        cost_center_id: newAllocation.cost_center_id,
        allocation_method: newAllocation.allocation_type === 'fixed' ? 'direct' : 'percentage',
        allocated_amount: newAllocation.allocation_type === 'fixed' ? parseFloat(newAllocation.amount) : 0,
        allocation_percentage: newAllocation.allocation_type === 'percentage' ? parseFloat(newAllocation.percentage) : null,
        notes: newAllocation.description || null,
        linked_objective_id: newAllocation.linked_objective_id || null,
        linked_initiative_id: newAllocation.linked_initiative_id || null,
        linked_revenue_stream_id: newAllocation.linked_revenue_stream_id || null
      });
      setNewAllocation({
        cost_center_id: '',
        amount: 0,
        percentage: 0,
        allocation_type: 'fixed',
        description: '',
        linked_objective_id: '',
        linked_initiative_id: '',
        linked_revenue_stream_id: ''
      });
      setEditingAllocation(null);
      setShowEditAllocation(false);
      toast.success('Cost allocation updated successfully');
      onRefresh();
    } catch (error) {
      console.error('Error updating allocation:', error);
      toast.error(error.response?.data?.detail || 'Failed to update cost allocation');
    }
  };

  const handleDeleteAllocation = async (allocationId) => {
    if (!window.confirm('Are you sure you want to delete this cost allocation?')) {
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/cost-allocations/${allocationId}`);
      toast.success('Cost allocation deleted successfully');
      onRefresh();
    } catch (error) {
      console.error('Error deleting allocation:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete cost allocation');
    }
  };

  const handleCancelEdit = () => {
    setEditingAllocation(null);
    setShowEditAllocation(false);
    setNewAllocation({
      cost_center_id: '',
      amount: 0,
      percentage: 0,
      allocation_type: 'fixed',
      description: '',
      linked_objective_id: '',
      linked_initiative_id: '',
      linked_revenue_stream_id: ''
    });
  };

  // Helper function to get SF item names
  const getSFItemNames = (allocation) => {
    const items = [];
    
    if (allocation.linked_objective_id) {
      const objective = sfHierarchy.find(obj => obj.objective_id === allocation.linked_objective_id);
      if (objective) {
        items.push({ type: 'Objective', name: objective.objective_name });
      }
    }
    
    if (allocation.linked_initiative_id) {
      for (const obj of sfHierarchy) {
        const initiative = obj.initiatives?.find(ini => ini.initiative_id === allocation.linked_initiative_id);
        if (initiative) {
          items.push({ type: 'Initiative', name: initiative.initiative_name });
          break;
        }
      }
    }
    
    if (allocation.linked_revenue_stream_id) {
      for (const obj of sfHierarchy) {
        for (const ini of obj.initiatives || []) {
          const stream = ini.revenue_streams?.find(rs => rs.stream_id === allocation.linked_revenue_stream_id);
          if (stream) {
            items.push({ type: 'Revenue Stream', name: stream.stream_name });
            break;
          }
        }
      }
    }
    
    return items;
  };

  return (
    <div className="space-y-6">
      {/* Create/Edit Allocation Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Target className="h-5 w-5 mr-2" />
            {showEditAllocation ? 'Edit Cost Allocation' : 'Create Cost Allocation'}
          </CardTitle>
          <CardDescription>
            {showEditAllocation ? 'Update the cost allocation details' : 'Allocate budget to cost centers'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <TooltipLabel htmlFor="allocation-center" tooltip={costCenterTooltips.allocationCostCenter}>
                Cost Center *
              </TooltipLabel>
              <Select value={newAllocation.cost_center_id} onValueChange={(value) => setNewAllocation({...newAllocation, cost_center_id: value})}>
                <SelectTrigger id="allocation-center">
                  <SelectValue placeholder="Select cost center" />
                </SelectTrigger>
                <SelectContent>
                  {costCenters.map(center => (
                    <SelectItem key={center.id} value={center.id}>
                      {center.name} {center.code && `(${center.code})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <TooltipLabel htmlFor="allocation-type" tooltip={costCenterTooltips.allocationType}>
                Allocation Type
              </TooltipLabel>
              <Select value={newAllocation.allocation_type} onValueChange={(value) => setNewAllocation({...newAllocation, allocation_type: value})}>
                <SelectTrigger id="allocation-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">💵 Fixed Amount</SelectItem>
                  <SelectItem value="percentage">📊 Percentage</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <TooltipLabel 
                htmlFor="allocation-value" 
                tooltip={newAllocation.allocation_type === 'fixed' ? costCenterTooltips.allocationAmount : costCenterTooltips.allocationPercentage}
              >
                {newAllocation.allocation_type === 'fixed' ? 'Amount (USD)' : 'Percentage (%)'}
              </TooltipLabel>
              <Input
                id="allocation-value"
                type="number"
                step={newAllocation.allocation_type === 'fixed' ? '0.01' : '0.1'}
                min="0"
                value={newAllocation.allocation_type === 'fixed' ? newAllocation.amount : newAllocation.percentage}
                onChange={(e) => setNewAllocation({
                  ...newAllocation, 
                  [newAllocation.allocation_type === 'fixed' ? 'amount' : 'percentage']: parseFloat(e.target.value) || 0
                })}
                placeholder={newAllocation.allocation_type === 'fixed' ? '0.00' : '25.0'}
              />
            </div>
            <div className="space-y-2">
              <TooltipLabel htmlFor="allocation-description" tooltip={costCenterTooltips.allocationDescription}>
                Description
              </TooltipLabel>
              <Input
                id="allocation-description"
                value={newAllocation.description}
                onChange={(e) => setNewAllocation({...newAllocation, description: e.target.value})}
                placeholder="e.g., Q1 Marketing Campaign"
              />
            </div>
          </div>

          {/* Strategic Framework Linking */}
          <div className="border-t pt-4 space-y-3">
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-1 flex items-center gap-2">
                <ArrowRight className="h-4 w-4 text-blue-600" />
                Link to Strategic Framework (Optional)
              </h3>
              <p className="text-xs text-gray-500">
                {helpText.strategicAlignment}
              </p>
            </div>
            <StrategicFrameworkLinker 
              form={newAllocation}
              setForm={setNewAllocation}
              sfHierarchy={sfHierarchy}
            />
          </div>
          
          <div className="flex gap-2">
            {showEditAllocation ? (
              <>
                <Button onClick={handleUpdateAllocation} disabled={!newAllocation.cost_center_id}>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Update Allocation
                </Button>
                <Button variant="outline" onClick={handleCancelEdit}>
                  Cancel
                </Button>
              </>
            ) : (
              <Button onClick={handleCreateAllocation} disabled={!newAllocation.cost_center_id}>
                <Plus className="h-4 w-4 mr-2" />
                Create Allocation
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Existing Allocations */}
      <Card>
        <CardHeader>
          <CardTitle>Current Allocations</CardTitle>
          <CardDescription>Manage budget allocations across cost centers</CardDescription>
        </CardHeader>
        <CardContent>
          {allocations.length > 0 ? (
            <div className="space-y-2">
              {allocations.map((allocation, index) => {
                const center = costCenters.find(c => c.id === allocation.cost_center_id);
                // Determine allocation type and value from backend fields
                const isPercentage = allocation.allocation_method === 'percentage';
                const displayValue = isPercentage 
                  ? `${allocation.allocation_percentage || 0}%` 
                  : formatCurrency(allocation.allocated_amount || 0);
                const methodLabel = isPercentage ? 'Percentage' : 'Fixed Amount';
                
                const sfItems = getSFItemNames(allocation);
                
                return (
                  <div key={allocation.id || index} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-gray-900">{center?.name || 'Unknown Center'}</h4>
                        <Badge variant="outline" className="text-xs">{methodLabel}</Badge>
                      </div>
                      {allocation.notes && (
                        <p className="text-sm text-gray-500 mt-1">{allocation.notes}</p>
                      )}
                      {sfItems.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {sfItems.map((item, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              <ArrowRight className="h-3 w-3 mr-1" />
                              {item.type}: {item.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-semibold text-lg text-gray-900">{displayValue}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleEditAllocation(allocation)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteAllocation(allocation.id)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-gradient-to-b from-slate-50 to-white rounded-lg border-2 border-dashed border-slate-200">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
                <Target className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Allocations Yet</h3>
              <p className="text-slate-600 mb-1">Start allocating budgets to your cost centers</p>
              <p className="text-sm text-slate-500 max-w-md mx-auto">
                Use the form above to create your first cost allocation and track budget distribution
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const BudgetManagementView = ({ costCenters, allocations, currentPlan, formatCurrency, onRefresh }) => {
  const [budgetPeriod, setBudgetPeriod] = useState('annual');
  const [selectedCenter, setSelectedCenter] = useState('');
  const [budgetUpdate, setBudgetUpdate] = useState({ budget_annual: 0, budget_quarterly: [0,0,0,0] });

  const updateBudget = async (centerId, budgetData) => {
    try {
      await axios.put(`${BACKEND_URL}/api/cost-centers/${centerId}`, budgetData);
      onRefresh();
    } catch (error) {
      console.error('Error updating budget:', error);
    }
  };

  const calculateBudgetUtilization = (center) => {
    const centerAllocations = allocations.filter(a => a.cost_center_id === center.id);
    const totalAllocated = centerAllocations.reduce((sum, a) => sum + (a.allocated_amount || 0), 0);
    const utilization = center.budget_annual > 0 ? (totalAllocated / center.budget_annual) * 100 : 0;
    return { totalAllocated, utilization: Math.min(utilization, 100) };
  };

  return (
    <div className="space-y-6">
      {/* Budget Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Budget</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(costCenters.reduce((sum, c) => sum + (c.budget_annual || 0), 0))}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700 font-medium">Total Allocated</p>
                <p className="text-2xl font-bold text-blue-900">
                  {formatCurrency(allocations.reduce((sum, a) => sum + (a.allocated_amount || 0), 0))}
                </p>
              </div>
              <Target className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-700 font-medium">Overall Utilization</p>
                <p className="text-2xl font-bold text-purple-900">
                  {((allocations.reduce((sum, a) => sum + (a.allocated_amount || 0), 0) / 
                     Math.max(costCenters.reduce((sum, c) => sum + (c.budget_annual || 0), 0), 1)) * 100).toFixed(1)}%
                </p>
              </div>
              <Activity className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Budget Details by Cost Center */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <BarChart3 className="h-5 w-5 mr-2" />
            Budget Details by Cost Center
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {costCenters.map((center) => {
              const { totalAllocated, utilization } = calculateBudgetUtilization(center);
              const remaining = (center.budget_annual || 0) - totalAllocated;
              
              return (
                <div key={center.id} className="border-2 border-slate-200 rounded-lg p-5 hover:border-blue-300 hover:shadow-md transition-all duration-200 bg-white">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-blue-100 border border-blue-200">
                        <Building2 className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">{center.name}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">{center.code}</Badge>
                          <Badge className="text-xs bg-slate-100 text-slate-700">{center.type}</Badge>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500 mb-1">Annual Budget</p>
                      <p className="text-xl font-bold text-slate-900">{formatCurrency(center.budget_annual || 0)}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-slate-50 rounded-lg">
                    <div className="text-center">
                      <p className="text-xs text-slate-600 mb-1 font-medium">Allocated</p>
                      <p className="text-lg font-bold text-blue-600">{formatCurrency(totalAllocated)}</p>
                    </div>
                    <div className="text-center border-x border-slate-200">
                      <p className="text-xs text-slate-600 mb-1 font-medium">Remaining</p>
                      <p className={`text-lg font-bold ${remaining >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(Math.abs(remaining))}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-600 mb-1 font-medium">Utilization</p>
                      <p className={`text-lg font-bold ${
                        utilization > 100 ? 'text-red-600' : 
                        utilization > 90 ? 'text-yellow-600' : 
                        utilization > 75 ? 'text-blue-600' :
                        'text-green-600'
                      }`}>
                        {utilization.toFixed(1)}%
                      </p>
                    </div>
                  </div>
                  
                  {/* Enhanced Progress Bar */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-600 font-medium">Budget Status</span>
                      <span className={`font-semibold ${
                        utilization > 100 ? 'text-red-600' : 
                        utilization > 90 ? 'text-yellow-600' : 
                        'text-green-600'
                      }`}>
                        {utilization > 100 ? 'Over Budget' : utilization > 90 ? 'Near Limit' : 'On Track'}
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                      <div 
                        className={`h-3 rounded-full transition-all duration-500 ease-out ${
                          utilization > 100 ? 'bg-gradient-to-r from-red-500 to-red-600' : 
                          utilization > 90 ? 'bg-gradient-to-r from-yellow-400 to-yellow-500' : 
                          'bg-gradient-to-r from-green-400 to-green-500'
                        }`}
                        style={{ width: `${Math.min(utilization, 100)}%` }}
                      />
                    </div>
                  </div>
                  
                  {/* Alert messages */}
                  {utilization > 100 && (
                    <div className="mt-3 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-red-800">Over Budget</p>
                        <p className="text-xs text-red-600 mt-1">
                          Exceeded by {formatCurrency(totalAllocated - (center.budget_annual || 0))}
                        </p>
                      </div>
                    </div>
                  )}
                  {utilization > 90 && utilization <= 100 && (
                    <div className="mt-3 flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-yellow-800">Approaching Limit</p>
                        <p className="text-xs text-yellow-600 mt-1">
                          Only {formatCurrency(remaining)} remaining
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Enhanced CostCenterNode component with modern styling
const CostCenterNode = ({ center, level, allocations, onEdit, onDelete, formatCurrency, calculateCenterMetrics }) => {
  const metrics = calculateCenterMetrics(center.id);
  const indent = level * 24;
  const utilization = center.budget_annual > 0 ? (metrics.allocatedAmount / center.budget_annual) * 100 : 0;
  
  // Get type color
  const getTypeColor = (type) => {
    const colors = {
      department: 'bg-blue-100 text-blue-700 border-blue-200',
      division: 'bg-purple-100 text-purple-700 border-purple-200',
      team: 'bg-green-100 text-green-700 border-green-200',
      custom: 'bg-gray-100 text-gray-700 border-gray-200'
    };
    return colors[type] || colors.custom;
  };

  return (
    <div className="space-y-2">
      <Card className="border-slate-200 hover:border-blue-300 hover:shadow-md transition-all duration-200">
        <CardContent className="p-4">
          <div className="flex items-start justify-between" style={{ marginLeft: `${indent}px` }}>
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <div className={`p-2 rounded-lg ${getTypeColor(center.type).split(' ')[0]} border ${getTypeColor(center.type).split(' ')[2]}`}>
                  <Building2 className={`h-4 w-4 ${getTypeColor(center.type).split(' ')[1]}`} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-slate-900">{center.name}</h4>
                    <Badge variant="outline" className="text-xs">
                      {center.code}
                    </Badge>
                    <Badge className={`text-xs ${getTypeColor(center.type)}`}>
                      {center.type}
                    </Badge>
                  </div>
                  {center.manager && (
                    <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {center.manager}
                    </p>
                  )}
                </div>
              </div>
              {center.description && (
                <p className="text-sm text-slate-600 mt-2 ml-11 pl-1 border-l-2 border-slate-200">{center.description}</p>
              )}
              
              {/* Budget Info */}
              <div className="flex items-center gap-4 mt-3 ml-11 pl-1">
                <div className="flex items-center gap-2 text-sm">
                  <DollarSign className="h-4 w-4 text-slate-400" />
                  <span className="text-slate-600">Budget:</span>
                  <span className="font-semibold text-slate-900">{formatCurrency(center.budget_annual || 0)}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Activity className="h-4 w-4 text-slate-400" />
                  <span className="text-slate-600">Allocated:</span>
                  <span className={`font-semibold ${utilization > 100 ? 'text-red-600' : utilization > 90 ? 'text-yellow-600' : 'text-green-600'}`}>
                    {formatCurrency(metrics.allocatedAmount)}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Target className="h-4 w-4 text-slate-400" />
                  <span className="text-slate-600">{metrics.allocationCount} allocation{metrics.allocationCount !== 1 ? 's' : ''}</span>
                </div>
              </div>
              
              {/* Progress bar */}
              {center.budget_annual > 0 && (
                <div className="mt-3 ml-11 pl-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-slate-500">Utilization</span>
                    <span className={`text-xs font-medium ${utilization > 100 ? 'text-red-600' : utilization > 90 ? 'text-yellow-600' : 'text-green-600'}`}>
                      {utilization.toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${
                        utilization > 100 ? 'bg-red-500' : 
                        utilization > 90 ? 'bg-yellow-500' : 
                        'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(utilization, 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-2 ml-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => onEdit(center)}
                className="h-8 w-8 p-0 hover:bg-blue-50 hover:text-blue-600"
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => onDelete(center.id)}
                className="h-8 w-8 p-0 hover:bg-red-50 hover:text-red-600"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Render children */}
      {center.children && center.children.length > 0 && (
        <div className="space-y-2">
          {center.children.map((child) => (
            <CostCenterNode
              key={child.id}
              center={child}
              level={level + 1}
              allocations={allocations}
              onEdit={onEdit}
              onDelete={onDelete}
              formatCurrency={formatCurrency}
              calculateCenterMetrics={calculateCenterMetrics}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CostCenterManagement;